import React, { useRef, useState } from "react";

import { useFormContext } from "react-hook-form";

import { Mic, Square, Loader2, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";

import { OPENAI_API_KEY } from "../../constants/secrets";
import { useStore } from "../../helpers/helpers";
import { buildProjectContext } from "./helpers/build-project-content";
import { createPrompt } from "./helpers/create-prompt";
import { matchArea, matchBooths, matchRequestTypes } from "./helpers/smart-matcher";
import { useTranslation } from "react-i18next";

const VoiceRecorder = () => {
    const mediaRecorderRef = useRef<any>(null);

    const audioChunksRef = useRef<any[]>([]);
    const { servicePortalStore } = useStore();
    const { qrDetails } = servicePortalStore;
    const [successFields, setSuccessFields] = useState<string[]>([]);
    const [voiceError, setVoiceError] = useState("");
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [audioURL, setAudioURL] = useState("");

    const [audioBlobData, setAudioBlobData] = useState<any>(null);

    const [transcript, setTranscript] = useState("");
    const { t } = useTranslation();
    const [pendingFields, setPendingFields] = useState<string[]>([]);

    const { setValue } = useFormContext();

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100,
                },
            });

            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: "audio/webm;codecs=opus",
            });

            mediaRecorderRef.current = mediaRecorder;

            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, {
                    type: "audio/webm",
                });

                const audioUrl = URL.createObjectURL(audioBlob);

                setAudioBlobData(audioBlob);

                setAudioURL(audioUrl);

                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();

            setTranscript("");

            setPendingFields([]);

            setSuccessFields([]);

            setIsRecording(true);
        } catch (error) {
            console.error("MIC ERROR:", error);

            alert("Microphone permission denied");
        }
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();

        setIsRecording(false);
    };

    const resetVoiceState = () => {
        setTranscript("");
        setPendingFields([]);
        setSuccessFields([]);
    };

    const showVoiceError = (message: string) => {
        resetVoiceState();
        setVoiceError(message);
    };

    const isPoorTranscript = (text: string) => {
        if (!text) return true;

        const cleaned = text.trim();

        // Too short
        if (cleaned.length < 20) return true;

        const words = cleaned.split(/\s+/);

        // Very few words
        if (words.length < 5) return true;

        // Contains replacement characters
        if (cleaned.includes("�")) return true;

        // Too many single-letter words
        const oneLetter = words.filter((w) => w.length === 1).length;
        if (oneLetter > words.length * 0.5) return true;

        // Same character repeated many times
        if (/(.)\1{7,}/.test(cleaned)) return true;

        // Lots of punctuation / symbols
        const symbols = (cleaned.match(/[^\p{L}\p{N}\s]/gu) || []).length;
        if (symbols > cleaned.length * 0.25) return true;

        return false;
    };

    const isPoorExtraction = (data: any) => {
        let score = 0;

        if (data.full_name) score++;
        if (data.phone) score++;
        if (data.gender) score++;
        if (data.address) score++;
        if (data.relation) score++;
        if (data.relative_name) score++;
        if (data.area) score++;
        if (data.dob || data.age) score++;

        if (Array.isArray(data.request_type) && data.request_type.length) score++;

        if (Array.isArray(data.village) && data.village.length) score++;

        return score < 3;
    };

    const processRecording = async () => {
        try {
            if (!audioBlobData) return;

            setIsProcessing(true);

            const formData = new FormData();

            formData.append("file", audioBlobData, "recording.webm");

            formData.append("model", "whisper-1");

            formData.append(
                "prompt",
                `
// This audio may contain:
// Kannada, Telugu, Tamil, Hindi or English.
//
// Transcribe accurately.
//
// Indian names, villages, addresses and phone numbers are important.
This is an Indian citizen grievance recording.

The speaker may speak Kannada, Telugu, Tamil, Hindi and English.

The recording contains:

- Full Name
- Phone Number
- WhatsApp Number
- Gender
- Date of Birth
- Village
- Area
- Address
- Relative Name
- Caste
- Government Schemes
- Pension
- Roads
- Water
- Drainage
- Electricity
- Education
- Health

Do not translate.

Preserve names exactly.

Preserve phone numbers digit by digit.

Preserve village names exactly.

Preserve caste names exactly.

Return only the transcript.
`
            );

            const whisperResponse = await fetch("https://api.openai.com/v1/audio/transcriptions", {
                method: "POST",

                headers: {
                    Authorization: `Bearer ${OPENAI_API_KEY}`,
                },

                body: formData,
            });
            console.log("KEY MNE MNE MNE:", OPENAI_API_KEY);
            const whisperResult = await whisperResponse.json();

            console.log("WHISPER RESULT:", whisperResult);

            // const transcriptText = whisperResult?.text || "";
            //
            // const projectContext = buildProjectContext(qrDetails);
            //
            // console.log(projectContext);
            const transcriptText = whisperResult?.text || "";

            if (isPoorTranscript(transcriptText)) {
                showVoiceError(
                    "We couldn't understand your recording. Please speak clearly and record again."
                );

                setIsProcessing(false);
                return;
            }

            setTranscript(transcriptText);

            const projectContext = buildProjectContext(qrDetails);

            console.log(projectContext);

            setTranscript(transcriptText);

            // =========================================
            // STEP 2 - FIELD EXTRACTION
            // =========================================

            const extractionResponse = await fetch("https://api.openai.com/v1/chat/completions", {
                method: "POST",

                headers: {
                    "Content-Type": "application/json",

                    Authorization: `Bearer ${OPENAI_API_KEY}`,
                },

                body: JSON.stringify({
                    model: "gpt-4o-mini",

                    response_format: {
                        type: "json_object",
                    },

                    messages: [
                        {
                            role: "system",

                            content: createPrompt(transcriptText, projectContext),
                        },
                        {
                            role: "user",

                            content: transcriptText,
                        },
                    ],

                    temperature: 0,
                }),
            });

            const extractionResult = await extractionResponse.json();

            console.log("GPT EXTRACTION:", extractionResult);

            const aiContent = extractionResult?.choices?.[0]?.message?.content || "{}";

            let parsedData: any = {};

            try {
                parsedData = JSON.parse(aiContent);
                const extractedFields = [
                    parsedData.full_name,
                    parsedData.phone,
                    parsedData.address,
                    parsedData.gender,
                    parsedData.relation,
                    parsedData.relative_name,
                    parsedData.dob,
                    parsedData.age,
                    parsedData.area,
                    ...(parsedData.village || []),
                    ...(parsedData.request_type || []),
                ].filter((v) => v !== "" && v !== null && v !== undefined);

                if (isPoorExtraction(parsedData)) {
                    showVoiceError(
                        "Unable to extract enough information from your recording. Please record again slowly in a quiet place."
                    );

                    setIsProcessing(false);
                    return;
                }
            } catch (error) {
                console.error("JSON PARSE ERROR:", error);

                alert("Unable to process voice");

                return;
            }

            console.log("PARSED DATA:", parsedData);

            // =========================================
            // PREFILL FORM
            // =========================================

            const filledFields: string[] = [];

            const missingFields: string[] = [];

            const safeSet = (key: string, value: any) => {
                if (value !== undefined && value !== null && value !== "") {
                    setValue(key, value);

                    filledFields.push(key);
                }
            };

            // =========================================
            // NAME
            // =========================================

            safeSet("full_name", parsedData?.full_name);

            // =========================================
            // PHONE
            // =========================================

            // PHONE
            if (parsedData?.phone) {
                const cleanedPhone = String(parsedData.phone).replace(/\D/g, "");

                console.log("PHONE LENGTH", cleanedPhone.length);

                console.log("PHONE VALUE", cleanedPhone);

                if (cleanedPhone.length === 10) {
                    console.log("INSIDE PHONE IF");

                    setValue("phone", cleanedPhone);

                    console.log("SETVALUE DONE");

                    filledFields.push("phone");

                    console.log("PUSH DONE");
                } else {
                    console.log("PHONE INVALID");
                }
            }

            // WHATSAPP
            if (parsedData?.whatsapp) {
                const cleanedWhatsapp = String(parsedData.whatsapp).replace(/\D/g, "");

                if (cleanedWhatsapp.length === 10) {
                    setValue("whatsapp", cleanedWhatsapp);
                }
            }
            console.log("PHONE:", parsedData.phone);
            console.log("WHATSAPP:", parsedData.whatsapp);
            console.log(parsedData);
            // =========================================
            // GENDER
            // =========================================

            if (parsedData?.gender) {
                setValue("gender", parsedData.gender.toLowerCase().trim());

                filledFields.push("gender");
            }

            // =========================================
            // AGE
            // =========================================

            if (parsedData?.age) {
                const cleanedAge = String(parsedData.age).replace(/\D/g, "");

                if (cleanedAge) {
                    setValue("age", cleanedAge);

                    filledFields.push("age");
                }
            }

            // =========================================
            // DOB
            // =========================================

            if (parsedData?.dob) {
                let dobValue = String(parsedData.dob).trim();

                // DD/MM/YYYY

                if (dobValue.includes("/")) {
                    const parts = dobValue.split("/");

                    if (parts.length === 3) {
                        dobValue = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
                    }
                }

                // DD-MM-YYYY
                else if (dobValue.includes("-") && dobValue.split("-")[0].length === 2) {
                    const parts = dobValue.split("-");

                    dobValue = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
                }

                const isValidDOB = /^\d{4}-\d{2}-\d{2}$/.test(dobValue);

                if (isValidDOB) {
                    setValue("dob", dobValue);

                    const dob = new Date(dobValue);

                    const today = new Date();

                    let age = today.getFullYear() - dob.getFullYear();

                    const monthDiff = today.getMonth() - dob.getMonth();

                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                        age--;
                    }

                    setValue("age", String(age));

                    filledFields.push("dob");
                }
            }

            // =========================================
            // ADDRESS
            // =========================================

            if (parsedData?.address) {
                let cleanedAddress = parsedData.address;

                cleanedAddress = cleanedAddress.replace(/\s+/g, " ");

                cleanedAddress = cleanedAddress.trim();

                setValue("address", cleanedAddress);

                filledFields.push("address");
            }

            // =========================================
            // ADDITIONAL INFO
            // =========================================

            safeSet("additionalInfo", parsedData?.additionalInfo);

            // =========================================
            // REQUEST TYPE
            // =========================================

            // if (
            //     parsedData?.request_type &&
            //     Array.isArray(parsedData.request_type) &&
            //     parsedData.request_type.length > 0
            // ) {
            //     setValue("request_type", parsedData.request_type);
            //
            //     filledFields.push("request_type");
            // } else {
            //     missingFields.push("request_type");
            // }

            // REQUEST TYPE
            if (
                parsedData?.request_type &&
                Array.isArray(parsedData.request_type) &&
                parsedData.request_type.length > 0
            ) {
                // const formattedRequestTypes = parsedData.request_type.map((item) => ({
                //     label: item,
                //     value: item,
                // }));
                const matchedRequestTypes = matchRequestTypes(parsedData.request_type, projectContext);

                if (matchedRequestTypes.length > 0) {
                    setValue("request_type", matchedRequestTypes);
                    filledFields.push("request_type");
                } else {
                    missingFields.push("request_type");
                }

                // setValue("request_type", formattedRequestTypes);

                filledFields.push("request_type");
            } else {
                missingFields.push("request_type");
            }

            // =========================================
            // RELATION
            // =========================================

            if (parsedData?.relation) {
                setValue("relation", parsedData.relation);

                filledFields.push("relation");
            } else {
                missingFields.push("relation");
            }

            // RELATIVE NAME

            // if (parsedData?.relative_name) {
            //     // setValue("relative_name", parsedData.relative_name);
            //     const matchedArea = matchArea(parsedData.area, projectContext);
            //
            //     if (matchedArea) {
            //         setValue("area", matchedArea);
            //         filledFields.push("area");
            //     } else {
            //         missingFields.push("area");
            //     }
            //
            //     filledFields.push("relative_name");
            // }

            if (parsedData?.relation) {
                setValue("relation", parsedData.relation);
                filledFields.push("relation");
            } else {
                missingFields.push("relation");
            }

            if (parsedData?.relative_name) {
                setValue("relative_name", parsedData.relative_name);
                filledFields.push("relative_name");
            } else {
                missingFields.push("relative_name");
            }
            // =========================================
            // AREA
            // =========================================

            if (parsedData?.area) {
                setValue("area", parsedData.area);

                filledFields.push("area");
            } else {
                missingFields.push("area");
            }

            // VILLAGE

            // VILLAGE

            // if (parsedData?.village && Array.isArray(parsedData.village) && parsedData.village.length > 0) {
            //     const formattedVillages = parsedData.village.map((item) => ({
            //         label: item,
            //         value: item,
            //         name: item,
            //     }));
            //
            //     setValue("village", formattedVillages);
            //
            //     filledFields.push("village");
            // } else {
            //     missingFields.push("village");
            // }

            // =========================================
            // VILLAGE SMART MATCHING
            // =========================================

            // if (parsedData?.village && Array.isArray(parsedData.village) && parsedData.village.length > 0) {
            //     // extract all villages from hierarchy
            //     let hierarchy = qrDetails?.hierarchy || [];
            //
            //     if (Array.isArray(hierarchy[0])) {
            //         hierarchy = hierarchy[0];
            //     }
            //
            //     let allVillages = [];
            //
            //     hierarchy.forEach((ac) => {
            //         allVillages.push(...(ac.children || []));
            //     });
            //
            //     // normalize function
            //     const normalize = (text = "") =>
            //         text
            //             .toLowerCase()
            //             .replace(/\s+/g, "")
            //             .replace(/village/g, "")
            //             .trim();
            //
            //     const matchedVillages = [];
            //
            //     parsedData.village.forEach((aiVillage) => {
            //         const normalizedAI = normalize(aiVillage);
            //
            //         // exact / partial match
            //         const foundVillage = allVillages.find((village) => {
            //             const normalizedVillage = normalize(village.label);
            //
            //             return (
            //                 normalizedVillage.includes(normalizedAI) ||
            //                 normalizedAI.includes(normalizedVillage)
            //             );
            //         });
            //
            //         if (foundVillage) {
            //             matchedVillages.push(foundVillage);
            //         }
            //     });
            //
            //     // remove duplicates
            //     const uniqueVillages = matchedVillages.filter(
            //         (v, index, self) => index === self.findIndex((x) => x.value === v.value)
            //     );
            //
            //     if (uniqueVillages.length > 0) {
            //         setValue("village", uniqueVillages);
            //
            //         filledFields.push("village");
            //     } else {
            //         missingFields.push("village");
            //     }
            // } else {
            //     missingFields.push("village");
            // }
            // =========================================
            // VILLAGE SMART MATCHING
            // =========================================

            if (parsedData?.village && Array.isArray(parsedData.village) && parsedData.village.length > 0) {
                let hierarchy = qrDetails?.hierarchy || [];

                if (Array.isArray(hierarchy[0])) {
                    hierarchy = hierarchy[0];
                }

                let allVillages = [];

                hierarchy.forEach((ac) => {
                    allVillages.push(...(ac.children || []));
                });

                console.log("ALL VILLAGES:", allVillages);

                console.log("AI PARSED VILLAGES:", parsedData.village);

                // =========================================
                // NORMALIZE
                // =========================================

                const normalize = (text = "") => {
                    return text
                        ?.toString()
                        ?.toLowerCase()

                        .replace(/\s+/g, "")
                        .replace(/[^\w]/g, "")

                        .replace(/ph/g, "f")
                        .replace(/bh/g, "b")
                        .replace(/dh/g, "d")
                        .replace(/th/g, "t")
                        .replace(/sh/g, "s")
                        .replace(/ck/g, "k")
                        .replace(/aa/g, "a")
                        .replace(/ee/g, "i")
                        .replace(/oo/g, "u")

                        .replace(/village/g, "")
                        .replace(/halli/g, "")
                        .replace(/kere/g, "")

                        .trim();
                };

                const matchedVillages = [];

                parsedData.village.forEach((aiVillage) => {
                    const normalizedAI = normalize(aiVillage);

                    console.log("AI VILLAGE:", aiVillage);

                    let foundVillage = null;

                    // =========================================
                    // EXACT MATCH
                    // =========================================

                    foundVillage = allVillages.find((village) => {
                        return normalize(village.label) === normalizedAI;
                    });

                    // =========================================
                    // PARTIAL MATCH
                    // =========================================

                    if (!foundVillage) {
                        foundVillage = allVillages.find((village) => {
                            const villageNorm = normalize(village.label);

                            return villageNorm.includes(normalizedAI) || normalizedAI.includes(villageNorm);
                        });
                    }

                    // =========================================
                    // CHARACTER SCORE MATCH
                    // =========================================

                    if (!foundVillage) {
                        let bestMatch = null;

                        let bestScore = 0;

                        allVillages.forEach((village) => {
                            const villageNorm = normalize(village.label);

                            let score = 0;

                            for (let i = 0; i < normalizedAI.length; i++) {
                                if (villageNorm.includes(normalizedAI[i])) {
                                    score++;
                                }
                            }

                            if (score > bestScore) {
                                bestScore = score;
                                bestMatch = village;
                            }
                        });

                        if (bestScore >= 4) {
                            foundVillage = bestMatch;
                        }
                    }

                    console.log("MATCHED VILLAGE:", foundVillage);

                    if (foundVillage) {
                        matchedVillages.push(foundVillage);
                    }
                });

                // REMOVE DUPLICATES

                const uniqueVillages = matchedVillages.filter(
                    (v, index, self) => index === self.findIndex((x) => x.value === v.value)
                );

                if (uniqueVillages.length > 0) {
                    setValue("village", uniqueVillages);

                    filledFields.push("village");
                } else {
                    missingFields.push("village");
                }
            } else {
                missingFields.push("village");
            }

            // =========================================
            // BOOTH
            // =========================================
            //
            // if (parsedData?.booth && Array.isArray(parsedData.booth) && parsedData.booth.length > 0) {
            //     // setValue("booth", parsedData.booth);
            //     const matchedBooths = matchBooths(parsedData.booth, projectContext);
            //
            //     if (matchedBooths.length > 0) {
            //         setValue("booth", matchedBooths);
            //         filledFields.push("booth");
            //     } else {
            //         missingFields.push("booth");
            //     }
            //
            //     filledFields.push("booth");
            // } else {
            //     missingFields.push("booth");
            // }
            // =========================================
            // CASTE (OPTIONAL)
            // =========================================

            if (parsedData?.caste) {
                const aiCaste = parsedData.caste.toLowerCase().trim();

                const matchedCaste = qrDetails?.caste?.find(
                    (item) =>
                        item.label.toLowerCase().includes(aiCaste) ||
                        aiCaste.includes(item.label.toLowerCase())
                );

                if (matchedCaste) {
                    setValue("caste", matchedCaste);
                    filledFields.push("caste");
                }
            }

            setPendingFields(missingFields);

            setSuccessFields(filledFields);

            setVoiceError("");
        } catch (error) {
            console.error("VOICE AI ERROR:", error);

            showVoiceError("Something went wrong while processing your voice. Please record again.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="rounded-[28px] border border-[#ECECEC] bg-[#FAFAFF] p-5">
            {/* HEADER */}

            <div className="text-center">
                <div className="inline-flex items-center rounded-full bg-[#F3EEFF] px-3 py-1 text-xs font-medium text-[#6D4DFF]">
                    {t("service_portal.aiAssistedComplaint")}
                </div>

                <h2 className="mt-4 text-xl font-semibold text-[#1B1B1D]">
                    {t("service_portal.speakInsteadOfTyping")}
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-gray-500">
                    {t("service_portal.recordGrievance")}
                </p>
            </div>

            {/* GUIDE */}

            <div className="mt-5 rounded-2xl border border-[#E4E4E7] bg-white p-4">
                <p className="text-sm font-semibold text-[#1B1B1D]">{t("voice_assistant.pleaseMention")}</p>

                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                    <li>• {t("voice_assistant.fullName")}</li>

                    <li>• {t("voice_assistant.relation")}</li>

                    <li>• {t("voice_assistant.phoneNumber")}</li>

                    <li>• {t("voice_assistant.gender")}</li>

                    <li>• {t("voice_assistant.dob")}</li>

                    <li>• {t("voice_assistant.village")}</li>

                    <li>• {t("voice_assistant.caste")}</li>

                    <li>• {t("voice_assistant.area")}</li>

                    <li>• {t("voice_assistant.whatsapp")}</li>

                    <li>• {t("voice_assistant.address")}</li>

                    <li>• {t("voice_assistant.issue")}</li>
                </ul>
            </div>

            <div className="mt-5 rounded-2xl border border-[#E4E4E7] bg-white p-4">
                <h3 className="text-sm font-semibold text-[#1B1B1D]">
                    📹 {t("voice_assistant.watchSample")}
                </h3>

                <p className="mt-1 text-xs text-gray-500">{t("voice_assistant.sampleDescription")}</p>

                <video controls className="mt-3 w-full rounded-xl" poster="/images/demo-thumbnail.jpg">
                    <source src="/videos/demo-recording.mp4" type="video/mp4" />
                </video>
            </div>

            {/* RECORD BUTTON */}

            <div className="mt-6 flex justify-center">
                {!isRecording ? (
                    <button
                        type="button"
                        onClick={startRecording}
                        className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-[#6D4DFF] to-[#8B6DFF] text-white shadow-lg transition-all hover:scale-105"
                    >
                        <Mic size={28} />
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={stopRecording}
                        className="flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-red-500 text-white shadow-lg"
                    >
                        <Square size={24} />
                    </button>
                )}
            </div>

            {/* STATUS */}

            <div className="mt-4 text-center">
                {isRecording && (
                    <p className="text-sm font-medium text-red-500">🔴 {t("voice_assistant.recording")}</p>
                )}

                {isProcessing && (
                    <div className="flex items-center justify-center gap-2 text-sm text-[#6D4DFF]">
                        <Loader2 className="animate-spin" size={16} />

                        {t("voice_assistant.processing")}
                    </div>
                )}
            </div>

            {/* AUDIO */}

            {audioURL && !isRecording && (
                <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-[#1B1B1D]">
                            {t("voice_assistant.yourRecording")}
                        </p>

                        <button
                            type="button"
                            onClick={() => {
                                setAudioURL("");

                                setAudioBlobData(null);

                                setTranscript("");

                                setPendingFields([]);

                                setSuccessFields([]);
                            }}
                            className="flex items-center gap-1 text-xs font-semibold text-red-500"
                        >
                            <Trash2 size={14} />

                            {t("voice_assistant.reRecord")}
                        </button>
                    </div>

                    <audio controls src={audioURL} className="mt-4 w-full" />

                    <button
                        type="button"
                        onClick={processRecording}
                        disabled={isProcessing}
                        className="mt-4 w-full rounded-xl bg-[#6D4DFF] py-3 font-medium text-white transition-all hover:opacity-90 disabled:opacity-50"
                    >
                        {isProcessing
                            ? t("voice_assistant.processingButton")
                            : t("voice_assistant.useRecording")}
                    </button>
                </div>
            )}

            {voiceError && (
                <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-5">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="mt-1 text-red-600" size={22} />

                        <div>
                            <h3 className="font-semibold text-red-700">
                                {t("voice_assistant.unableProcess")}
                            </h3>

                            <p className="mt-2 text-sm leading-6 text-red-600">{voiceError}</p>

                            <button
                                type="button"
                                onClick={() => {
                                    setVoiceError("");

                                    setAudioURL("");

                                    setAudioBlobData(null);

                                    setTranscript("");

                                    setPendingFields([]);

                                    setSuccessFields([]);
                                }}
                                className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                            >
                                {t("voice_assistant.recordAgain")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TRANSCRIPT */}

            {transcript && (
                <div className="mt-5 rounded-2xl border border-[#E5E7EB] bg-white p-4">
                    <p className="text-sm font-semibold text-[#1B1B1D]">{t("voice_assistant.transcript")}</p>

                    <p className="mt-3 text-sm leading-relaxed text-gray-600">{transcript}</p>
                </div>
            )}

            {/* SUCCESS */}

            {successFields.length > 0 && (
                <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-4">
                    <div className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 text-green-600" />

                        <div>
                            <p className="font-semibold text-green-900">AI Filled Successfully</p>

                            <div className="mt-3 flex flex-wrap gap-2">
                                {successFields.map((item, index) => (
                                    <div
                                        key={index}
                                        className="rounded-full bg-white px-3 py-1 text-xs font-medium text-green-700"
                                    >
                                        {item.replace("_", " ")}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MANUAL SELECTION */}

            {pendingFields.length > 0 && (
                <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 text-amber-600" />

                        <div className="flex-1">
                            <p className="font-semibold text-amber-900">Manual Selection Required</p>

                            <p className="mt-1 text-sm text-amber-700">
                                Please select the remaining fields manually.
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                                {pendingFields.map((field, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        onClick={() => {
                                            const element = document.getElementById(`${field}-section`);

                                            if (element) {
                                                element.scrollIntoView({
                                                    behavior: "smooth",
                                                    block: "center",
                                                });
                                            }
                                        }}
                                        className="rounded-full bg-white px-3 py-2 text-xs font-semibold text-amber-900 shadow-sm transition-all hover:scale-105"
                                    >
                                        0 Select {field.replace("_", " ")}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default VoiceRecorder;
